import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import toast from 'react-hot-toast';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { apiService } from '../../services/apiService';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { QuestionCard } from './QuestionCard';
import { CompanyQuestionCard } from './CompanyQuestionCard';
import { API_LEVEL_FALLBACK } from '../../utils/vacancies/mapping';
import { questionTypeOptions } from '../../constants/vacancyConstants';
import { ArrowDownSLineIconV7 } from '../ui/figma-icons-v7';
import { questionVariantsSessionService } from '../../services/questionVariantsSessionService';
import { ReactComponent as FileCopyLineIcon } from '../../assets/icons/file-copy-line.svg';

interface InterviewQuestionsEditFormProps {
  vacancyId: number;
  onBack: () => void;
  onSaveSuccess?: (vacancyId: number) => void;
  hideActions?: boolean;
  onSavingChange?: (saving: boolean) => void;
  onReadyChange?: (ready: boolean) => void;
}

export interface InterviewQuestionsEditFormHandle {
  save: () => Promise<boolean>;
  getQuestionCount: () => number;
  isReady: () => boolean;
}

const resolveQuestionId = (id: unknown): number | undefined => {
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  if (typeof id === 'string' && /^\d+$/.test(id)) {
    return Number(id);
  }
  return undefined;
};

const normalizeQuestionForCompare = (q: any) => ({
  id: resolveQuestionId(q?.id) ?? null,
  text: (q?.text ?? '').trim(),
  order: Number(q?.order) || 0,
  isRequired: q?.isRequired !== false,
  evaluationCriteria: (q?.evaluationCriteria ?? '').trim(),
});

const hasInterviewQuestionsChanges = (current: any[], initial: any[]): boolean => {
  if (current.length !== initial.length) {
    return true;
  }
  return current.some((q, index) => {
    const initialQ = initial[index];
    if (!initialQ) {
      return true;
    }
    return (
      JSON.stringify(normalizeQuestionForCompare(q)) !==
      JSON.stringify(normalizeQuestionForCompare(initialQ))
    );
  });
};

const htmlToPlainText = (html: string) => {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  }
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const PDF_REPORT_SECTION_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'JUSTIFICATION', label: 'ОБОСНОВАНИЕ ОЦЕНКИ' },
  { key: 'CRITERIA_DETAILS', label: 'ДЕТАЛЬНАЯ ОЦЕНКА ПО КРИТЕРИЯМ' },
  { key: 'STRENGTHS', label: 'СИЛЬНЫЕ СТОРОНЫ' },
  { key: 'RECOMMENDATIONS', label: 'РЕКОМЕНДАЦИИ ДЛЯ РАЗВИТИЯ' },
  { key: 'TRANSCRIPT', label: 'Транскрибация ответа кандидата' },
];
const PDF_REPORT_SECTION_KEYS = PDF_REPORT_SECTION_OPTIONS.map((o) => o.key);

export const InterviewQuestionsEditForm = forwardRef(
  (
    { vacancyId, onBack, onSaveSuccess, hideActions, onSavingChange, onReadyChange }: InterviewQuestionsEditFormProps,
    ref: React.Ref<InterviewQuestionsEditFormHandle>
  ) => {
  const colors = useBrandingColors();

  // Form state
  const [vacancyDescription, setVacancyDescription] = useState('');
  const [vacancyLevel, setVacancyLevel] = useState('MIDDLE');
  const [aiDescription, setAiDescription] = useState('');
  const [timePerAnswer, setTimePerAnswer] = useState('2 мин');
  const [questionCount, setQuestionCount] = useState(5);
  const [questionCountInput, setQuestionCountInput] = useState('5');
  const [minScore, setMinScore] = useState(5);
  const [questions, setQuestions] = useState<any[]>([]);
  const [questionType, setQuestionType] = useState<string>('В основном хард-скиллы');
  const [isQuestionTypeDropdownOpen, setIsQuestionTypeDropdownOpen] = useState(false);
  const [pdfReportSections, setPdfReportSections] = useState<string[]>([...PDF_REPORT_SECTION_KEYS]);
  const [activeTab, setActiveTab] = useState<'interview' | 'company'>('interview');
  const [companyQuestions, setCompanyQuestions] = useState<Array<{
    id: string;
    question: string;
    answer: string;
  }>>([
    { id: `company-q-${Date.now()}-1`, question: '', answer: '' },
    { id: `company-q-${Date.now()}-2`, question: '', answer: '' },
    { id: `company-q-${Date.now()}-3`, question: '', answer: '' },
  ]);
  const [companyDescription, setCompanyDescription] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [isReadyToSave, setIsReadyToSave] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);

  const questionsGeneratorRef = React.useRef<HTMLDivElement>(null);
  const initialQuestionsRef = React.useRef<any[]>([]);

  // Load vacancy data and questions
  useEffect(() => {
    loadVacancyQuestionsData();
  }, [vacancyId]);

  useEffect(() => {
    if (onReadyChange) {
      onReadyChange(isReadyToSave);
    }
  }, [isReadyToSave, onReadyChange]);

  const loadVacancyQuestionsData = async () => {
    try {
      setIsLoading(true);
      setIsReadyToSave(false);

      const vacancyData = await apiService.getPosition(vacancyId);
      const rawDescription = vacancyData.description || '';
      const plainDescription = htmlToPlainText(rawDescription);
      setVacancyDescription(plainDescription);
      setAiDescription(plainDescription);
      setVacancyLevel(vacancyData.level || 'MIDDLE');
      setMinScore(vacancyData.minScore || 5);
      setCompanyDescription(htmlToPlainText(vacancyData.companyDescription || ''));

      const savedSections = (vacancyData as any).pdfReportSections as string[] | null | undefined;
      if (savedSections && savedSections.length > 0) {
        setPdfReportSections(
          savedSections.filter((key) => PDF_REPORT_SECTION_KEYS.includes(key))
        );
      } else {
        setPdfReportSections([...PDF_REPORT_SECTION_KEYS]);
      }

      try {
        const questionsResponse = await apiService.getPositionQuestionsWithSettings(vacancyId);
        const questionsData = questionsResponse.questions || [];
        const interviewSettings = questionsResponse.interviewSettings || {};

        const answerTime = interviewSettings.answerTime || vacancyData.answerTime || 120;
        const minutes = Math.floor(answerTime / 60);
        setTimePerAnswer(`${minutes} мин`);

        const apiLevel = interviewSettings.level || vacancyData.level || API_LEVEL_FALLBACK;
        setVacancyLevel((apiLevel || 'MIDDLE').toUpperCase());

        setMinScore(interviewSettings.minScore || vacancyData.minScore || 5);

        const rawQuestionsCount =
          interviewSettings.questionsCount || questionsData.length || 5;
        const clampedQuestionsCount = Math.min(15, Math.max(1, rawQuestionsCount));
        setQuestionCount(clampedQuestionsCount);
        setQuestionCountInput(String(clampedQuestionsCount));

        setQuestionType(interviewSettings.questionType || 'В основном хард-скиллы');

        const normalizedQuestions = questionsData.map((q: any) => ({
          ...q,
          evaluationCriteria: q.evaluationCriteria ?? ''
        }));
        setQuestions(normalizedQuestions);
        initialQuestionsRef.current = JSON.parse(JSON.stringify(normalizedQuestions));
      } catch (questionsError) {
        console.error('Ошибка при получении вопросов:', questionsError);
        throw questionsError;
      }

      // Загружаем вопросы о компании для вакансии
      try {
        const companyQuestionsResponse = await apiService.getPositionCompanyQuestions(vacancyId);
        const cqData = companyQuestionsResponse.questions || [];
        if (cqData.length > 0) {
          setCompanyQuestions(cqData.map((q: any, i: number) => ({
            id: `company-q-${Date.now()}-${i}`,
            question: q.question || '',
            answer: q.answer || ''
          })));
        } else {
          setCompanyQuestions([{ id: `company-q-${Date.now()}-1`, question: '', answer: '' }]);
        }
      } catch (cqError) {
        console.error('Ошибка при получении вопросов о компании:', cqError);
        throw cqError;
      }

      setIsReadyToSave(true);
      toast.success('Данные интервью загружены');
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      toast.error('Не удалось загрузить данные интервью');
    } finally {
      setIsLoading(false);
    }
  };


  const handleGenerateQuestions = async () => {
    if (!aiDescription.trim() && !vacancyDescription.trim()) {
      toast.error('Пожалуйста, введите описание для генерации вопросов');
      return;
    }

    setIsGeneratingQuestions(true);
    try {
      const descriptionForAI = aiDescription.trim() || vacancyDescription.trim();
      const targetCount = questions.length === 0 ? questionCount : 1;

      const questionsResponse = await apiService.generatePositionQuestions(
        descriptionForAI,
        targetCount
      );

      if (questionsResponse && questionsResponse.length > 0) {
        if (questions.length === 0) {
          const formattedQuestions = questionsResponse.map((question: string, index: number) => ({
            id: `question-${Date.now()}-${index}`,
            text: question,
            evaluationCriteria: '',
            isRequired: true,
            order: index + 1,
            isVoiced: false
          }));

          setQuestions(formattedQuestions);
          toast.success(`Сгенерировано ${formattedQuestions.length} вопросов`);
        } else {
          const baseLength = questions.length;
          const singleQuestion = questionsResponse[0];
          const newQuestion = {
            id: `question-${Date.now()}`,
            text: singleQuestion,
            evaluationCriteria: '',
            isRequired: true,
            order: baseLength + 1,
            isVoiced: false
          };

          setQuestions(prev => [...prev, newQuestion]);
          toast.success('Добавлен новый вопрос');
        }
      } else {
        toast.error('AI не смог сгенерировать вопросы');
      }
    } catch (error: any) {
      console.error('Ошибка генерации вопросов:', error);
      const backendMessage = error?.response?.data?.message;
      toast.error(backendMessage || 'Ошибка при генерации вопросов');
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const getQuestionHistoryKey = (question: any, index: number) => {
    if (question && question.id !== undefined && question.id !== null) {
      return String(question.id);
    }
    return `index-${index}`;
  };

  const handleRegenerateQuestion = async (questionIndex: number, currentQuestion: any) => {
    if (!aiDescription.trim() && !vacancyDescription.trim()) {
      toast.error('Пожалуйста, введите описание для регенерации');
      return;
    }

    try {
      const descriptionForAI = aiDescription.trim() || vacancyDescription.trim();

      const sourceQuestion = questions[questionIndex] || currentQuestion;
      const previousText = sourceQuestion?.text || currentQuestion.text;

      const newQuestionText = await apiService.generateQuestion(
        previousText,
        descriptionForAI
      );

      if (newQuestionText) {
        const questionKey = getQuestionHistoryKey(sourceQuestion, questionIndex);
        questionVariantsSessionService.addRegeneratedVariant(
          vacancyId,
          questionKey,
          previousText,
          newQuestionText
        );

        setQuestions(prev =>
          prev.map((q, i) =>
            i === questionIndex
              ? { ...q, text: newQuestionText, isVoiced: q.isVoiced || false }
              : q
          )
        );
        toast.success('Вопрос перегенерирован');
      } else {
        toast.error('AI не смог перегенерировать вопрос');
      }
    } catch (error: any) {
      console.error('Ошибка регенерации вопроса:', error);
      const backendMessage = error?.response?.data?.message;
      toast.error(backendMessage || 'Ошибка при регенерации вопроса');
    }
  };

  const handleShowPreviousVariant = (questionIndex: number) => {
    const current = questions[questionIndex];
    if (!current) {
      return;
    }
    const questionKey = getQuestionHistoryKey(current, questionIndex);
    const previousText = questionVariantsSessionService.getPreviousVariant(
      vacancyId,
      questionKey
    );
    if (!previousText) {
      return;
    }
    setQuestions(prev =>
      prev.map((q, i) =>
        i === questionIndex
          ? { ...q, text: previousText, isVoiced: q.isVoiced || false }
          : q
      )
    );
  };

  const handleShowNextVariant = (questionIndex: number) => {
    const current = questions[questionIndex];
    if (!current) {
      return;
    }
    const questionKey = getQuestionHistoryKey(current, questionIndex);
    const nextText = questionVariantsSessionService.getNextVariant(
      vacancyId,
      questionKey
    );
    if (!nextText) {
      return;
    }
    setQuestions(prev =>
      prev.map((q, i) =>
        i === questionIndex
          ? { ...q, text: nextText, isVoiced: q.isVoiced || false }
          : q
      )
    );
  };

  const handleAddQuestionManually = () => {
    const newQuestion = {
      id: `question-${Date.now()}`,
      text: '',
      evaluationCriteria: '',
      isRequired: true,
      order: questions.length + 1,
      isVoiced: false
    };
    setQuestions([...questions, newQuestion]);
  };

  const handleUpdateQuestion = (questionIndex: number, updates: any) => {
    setQuestions(prev =>
      prev.map((q, i) => (i === questionIndex ? { ...q, ...updates } : q))
    );
  };

  const handleRemoveQuestion = (questionIndex: number) => {
    setQuestions(prev => {
      const filtered = prev.filter((_, i) => i !== questionIndex);
      const reordered = filtered.map((q, index) => ({ ...q, order: index + 1 }));
      return reordered;
    });
  };

  const moveQuestion = (fromIndex: number, toIndex: number) => {
    setQuestions(prev => {
      const newQuestions = [...prev];
      const [movedQuestion] = newQuestions.splice(fromIndex, 1);
      newQuestions.splice(toIndex, 0, movedQuestion);
      return newQuestions.map((q, index) => ({ ...q, order: index + 1 }));
    });
  };

  const buildQuestionsPayload = () =>
    questions.map((q, index) => ({
      id: resolveQuestionId(q.id),
      text: q.text,
      order: q.order || index + 1,
      isRequired: q.isRequired !== false,
      evaluationCriteria: q.evaluationCriteria ?? '',
    }));

  const handleSave = async (): Promise<boolean> => {
    if (!isReadyToSave) {
      toast.error('Данные интервью ещё не загружены. Дождитесь завершения загрузки и попробуйте снова.');
      return false;
    }

    try {
      setIsSaving(true);
      if (onSavingChange) {
        onSavingChange(true);
      }

      let answerTime = 120;
      const timeMatch = timePerAnswer.match(/(\d+)\s*мин(?:\s+(\d+)\s*сек)?/);
      if (timeMatch) {
        const minutes = parseInt(timeMatch[1]) || 0;
        const seconds = parseInt(timeMatch[2]) || 0;
        answerTime = minutes * 60 + seconds;
      }

      const payload: any = {
        interviewSettings: {
          answerTime: answerTime,
          level: vacancyLevel as any,
          questionsCount: questionCount,
          questionType: questionType as any,
          minScore: minScore as any,
          pdfReportSections: pdfReportSections,
        } as any,
      };

      if (hasInterviewQuestionsChanges(questions, initialQuestionsRef.current)) {
        payload.questions = buildQuestionsPayload();
      }

      await apiService.updatePositionQuestionsAndSettings(vacancyId, payload);

      initialQuestionsRef.current = JSON.parse(JSON.stringify(questions));

      // Сохраняем вопросы о компании для вакансии
      const companyQuestionsPayload = companyQuestions
        .filter(cq => cq.question.trim() !== '') // Фильтруем пустые вопросы
        .map((cq, index) => ({
          question: cq.question,
          answer: cq.answer,
          orderIndex: index
        }));

      await apiService.updatePositionCompanyQuestions(vacancyId, {
        questions: companyQuestionsPayload
      });

      questionVariantsSessionService.clearVacancyHistory(vacancyId);

      toast.success(
        hideActions
          ? 'Настройки AI скрининга и вопросы сохранены'
          : 'Настройки интервью и вопросы успешно обновлены'
      );

      if (!hideActions) {
        if (onSaveSuccess) {
          onSaveSuccess(vacancyId);
        } else {
          onBack();
        }
      }
      return true;
    } catch (error: any) {
      console.error('Ошибка сохранения настроек:', error);
      const backendMessage = error?.response?.data?.message;
      toast.error(backendMessage || 'Ошибка при сохранении настроек');
      return false;
    } finally {
      setIsSaving(false);
      if (onSavingChange) {
        onSavingChange(false);
      }
    }
  };

  const handleCancel = () => {
    questionVariantsSessionService.clearVacancyHistory(vacancyId);
    onBack();
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    getQuestionCount: () => questions.length,
    isReady: () => isReadyToSave
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Загрузка данных интервью...</div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="bg-[#f5f6f1] min-h-screen py-0">
        <div className="w-full max-w-full px-0">
          <h2 className="text-xl font-semibold text-[#0a0d14] mb-4">
            Условия
          </h2>
          <div className="bg-white rounded-[12px] p-8 mb-6">
            {/* Minimum Score */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#0a0d14] mb-3">
                Минимальная оценка
              </label>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                  <button
                    key={score}
                    onClick={() => setMinScore(score)}
                    className="px-4 py-3 rounded-2xl text-base font-medium transition-all"
                    style={{
                      backgroundColor: score === minScore ? colors.primary : '#f6f8fa',
                      color: score === minScore ? '#ffffff' : '#0a0d14'
                    }}
                    onMouseEnter={(e) => {
                      if (score !== minScore)
                        (e.currentTarget as HTMLButtonElement).style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                    }}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </div>

            {/* Time per Answer */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#0a0d14] mb-3">
                Время на один ответ
              </label>
              <div className="flex flex-wrap gap-2">
                {['1 мин', '1 мин 30 сек', '2 мин', '2 мин 30 сек', '3 мин', '3 мин 30 сек', '4 мин', '5 мин'].map((time) => (
                  <button
                    key={time}
                    onClick={() => setTimePerAnswer(time)}
                    className="px-4 py-3 rounded-3xl text-base font-medium transition-all"
                    style={{
                      backgroundColor: time === timePerAnswer ? colors.primary : '#f6f8fa',
                      color: time === timePerAnswer ? '#ffffff' : '#0a0d14'
                    }}
                    onMouseEnter={(e) => {
                      if (time !== timePerAnswer)
                        (e.currentTarget as HTMLButtonElement).style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                    }}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>

            {/* Level */}
            <div>
              <label className="block text-sm font-medium text-[#0a0d14] mb-3">
                Уровень
              </label>
              <div className="flex flex-wrap gap-2">
                {['Junior', 'Middle', 'Senior', 'Lead'].map((level) => (
                  <button
                    key={level}
                    onClick={() => setVacancyLevel(level.toUpperCase())}
                    className="px-4 py-3 rounded-3xl text-base font-medium transition-all"
                    style={{
                      backgroundColor: level.toUpperCase() === vacancyLevel ? colors.primary : '#f6f8fa',
                      color: level.toUpperCase() === vacancyLevel ? '#ffffff' : '#0a0d14'
                    }}
                    onMouseEnter={(e) => {
                      if (level.toUpperCase() !== vacancyLevel)
                        (e.currentTarget as HTMLButtonElement).style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                    }}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* PDF Report Sections */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-[#0a0d14] mb-3">
                Разделы PDF-отчёта
              </label>
              <div className="flex flex-wrap gap-2">
                {PDF_REPORT_SECTION_OPTIONS.map((opt) => {
                  const active = pdfReportSections.includes(opt.key);
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() =>
                        setPdfReportSections((prev) =>
                          prev.includes(opt.key)
                            ? prev.filter((k) => k !== opt.key)
                            : [...prev, opt.key]
                        )
                      }
                      className="px-4 py-3 rounded-3xl text-base font-medium transition-all"
                      style={{
                        backgroundColor: active ? colors.primary : '#f6f8fa',
                        color: active ? '#ffffff' : '#0a0d14'
                      }}
                      onMouseEnter={(e) => {
                        if (!active)
                          (e.currentTarget as HTMLButtonElement).style.opacity = '0.8';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center gap-0 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('interview')}
              className="px-6 py-4 rounded-xl text-xl font-medium transition-colors"
              style={{
                backgroundColor: activeTab === 'interview' ? '#ffffff' : 'transparent',
                color: activeTab === 'interview' ? colors.primary : '#000000',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'interview') {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#e5e7eb';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'interview') {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                }
              }}
            >
              Вопросы для интервью
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('company')}
              className="px-6 py-4 rounded-xl text-xl font-medium transition-colors"
              style={{
                backgroundColor: activeTab === 'company' ? '#ffffff' : 'transparent',
                color: activeTab === 'company' ? colors.primary : '#000000',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'company') {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#e5e7eb';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'company') {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                }
              }}
            >
              Вопросы о компании
            </button>
          </div>

          {activeTab === 'interview' && (
            <>
          {/* AI Generator Section */}
          <h2 
            ref={questionsGeneratorRef} 
            className="text-xl font-semibold text-[#0a0d14] mb-4 scroll-mt-24"
          >
            Генерация вопросов с помощью AI
          </h2>
          <div className="bg-white rounded-[12px] p-8 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                  Опишите требования или дайте контекст для генерации
                </label>
                <textarea
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  className="w-full px-6 py-4 border border-[#e2e4e9] rounded-[16px] text-base min-h-[120px] resize-none focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  placeholder="Например: Нужны вопросы по микросервисной архитектуре и Spring Boot..."
                />
              </div>

              <div className="flex flex-col gap-4 md:flex-row md:items-end md:gap-[56px]">
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#0a0d14]">
                    Сколько вопросов сгенерировать?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[5, 10, 15].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setQuestionCount(value);
                          setQuestionCountInput(String(value));
                        }}
                        className="w-[46px] h-[52px] flex items-center justify-center rounded-[16px] text-base font-medium transition-all"
                        style={{
                          backgroundColor: value === questionCount ? colors.primary : '#f6f8fa',
                          color: value === questionCount ? '#ffffff' : '#0a0d14'
                        }}
                        onMouseEnter={(e) => {
                          if (value !== questionCount) {
                            (e.currentTarget as HTMLButtonElement).style.opacity = '0.85';
                          }
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                        }}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#0a0d14]">
                    Ввести вручную
                  </label>
                  <input
                    type="text"
                    value={questionCountInput}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s+/g, '');
                      if (value === '') {
                        setQuestionCountInput('');
                        return;
                      }
                      if (!/^\d+$/.test(value)) {
                        return;
                      }
                      const raw = Number(value);
                      const clamped = Math.min(15, Math.max(1, raw));
                      setQuestionCountInput(String(clamped));
                      setQuestionCount(clamped);
                    }}
                    onBlur={() => {
                      if (questionCountInput.trim() === '') {
                        setQuestionCount(1);
                        setQuestionCountInput('1');
                        return;
                      }
                      const raw = Number(questionCountInput);
                      const clamped = Number.isNaN(raw) ? 1 : Math.min(15, Math.max(1, raw));
                      setQuestionCount(clamped);
                      setQuestionCountInput(String(clamped));
                    }}
                    className="w-[46px] h-[52px] border border-[#e2e4e9] rounded-[16px] text-base text-center focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                  Что проверяем?
                </label>
                <div className="relative w-full">
                  <button
                    type="button"
                    onClick={() => setIsQuestionTypeDropdownOpen(!isQuestionTypeDropdownOpen)}
                    className="w-full flex items-center justify-between px-[14px] py-[14px] rounded-[16px] border border-[#e2e4e9] bg-white text-sm text-left shadow-[0_1px_2px_rgba(228,229,231,0.24)] focus:outline-none"
                  >
                    <span className="text-[#525866]">{questionType}</span>
                    <ArrowDownSLineIconV7 size={20} className="w-5 h-5 text-[#868c98] transition-transform" />
                  </button>
                  {isQuestionTypeDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsQuestionTypeDropdownOpen(false)}
                      />
                      <div className="absolute z-20 mt-1 w-full rounded-[16px] border border-[#e2e4e9] bg-white shadow-[0_4px_12px_rgba(15,15,16,0.12)] overflow-hidden">
                        {questionTypeOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              setQuestionType(option.label);
                              setIsQuestionTypeDropdownOpen(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-[#f6f8fa] transition-colors"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Questions Section */}
          {questions.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-[#0a0d14]">
                  Вопросы интервью ({questions.length})
                </h2>
              </div>
              <div className="mb-6">
                <div className="space-y-4">
                  {questions.map((question, index) => (
                    <QuestionCard
                      key={index}
                      question={{
                        id: question.id || `question-${index}`,
                        text: question.text,
                        evaluationCriteria: question.evaluationCriteria ?? '',
                        isRequired: question.isRequired !== false,
                        order: question.order || index + 1,
                        isVoiced: question.isVoiced || false
                      }}
                      index={index}
                      questions={questions}
                      setQuestions={setQuestions}
                      moveQuestion={moveQuestion}
                      onRegenerateQuestion={(questionIndex, currentQuestion) => handleRegenerateQuestion(questionIndex, currentQuestion)}
                      onDeleteQuestion={(questionIndex) => handleRemoveQuestion(questionIndex)}
                      onShowPreviousVariant={(questionIndex) => handleShowPreviousVariant(questionIndex)}
                      onShowNextVariant={(questionIndex) => handleShowNextVariant(questionIndex)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="mb-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleGenerateQuestions}
              disabled={isGeneratingQuestions}
              className="inline-flex items-center justify-center px-[14px] py-4 rounded-[16px] text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(180deg, #E16349 0%, #DF1C41 100%)'
              }}
            >
              {isGeneratingQuestions ? 'Генерация...' : 'Сгенерировать с AI'}
            </button>
            <button
              type="button"
              onClick={handleAddQuestionManually}
              className="inline-flex items-center justify-center px-[14px] py-4 rounded-[16px] text-sm font-medium border border-[#E16349] bg-white text-[#E16349] gap-2"
            >
              <span>+ Добавить вручную</span>
            </button>
          </div>
            </>
          )}

          {activeTab === 'company' && (
            <>
              {/* Company Questions Info Block */}
              <div className="bg-white rounded-[14px] px-6 py-5 mb-6">
                <p className="text-sm font-medium text-center text-[#0a0d14]">
                  В этом разделе вы можете добавить вопросы и ответы о компании или вакансии, которые будут доступны кандидату после завершения интервью.
                </p>
              </div>

              {/* Company Questions AI Generator */}
              <div className="bg-white rounded-[16px] p-6 mb-6">
                <p className="text-sm font-medium text-[#0a0d14] mb-2">
                  Вставьте описание вакансии или компании в форму ниже и нажмите "Сгенерировать"
                </p>
                <textarea
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  className="w-full px-6 py-6 border border-[#e2e4e9] rounded-[16px] text-base min-h-[210px] resize-none focus:outline-none shadow-[0_1px_2px_rgba(228,229,231,0.24)] placeholder:text-[#868c98]"
                  placeholder=""
                />
                <div className="flex items-center justify-between mt-6">
                  <div className="flex items-center gap-6">
                    <button
                      type="button"
                      onClick={() => {
                        setAiDescription(vacancyDescription);
                        toast.success('Описание вакансии вставлено');
                      }}
                      className="flex items-center gap-1 text-[14px] font-normal leading-[20px]"
                      style={{ 
                        fontFamily: 'Inter',
                        letterSpacing: '-0.006em',
                        color: colors.primary
                      }}
                    >
                      <FileCopyLineIcon className="w-4 h-4" style={{ color: colors.primary }} />
                      Вставить описание вакансии
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAiDescription(companyDescription);
                        toast.success('Описание компании вставлено');
                      }}
                      className="flex items-center gap-1 text-[14px] font-normal leading-[20px]"
                      style={{ 
                        fontFamily: 'Inter',
                        letterSpacing: '-0.006em',
                        color: colors.primary
                      }}
                    >
                      <FileCopyLineIcon className="w-4 h-4" style={{ color: colors.primary }} />
                      Вставить описание компании
                    </button>
                  </div>
                </div>
              </div>

              {/* Company Questions List */}
              {companyQuestions.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-[#0a0d14]">
                      Вопросы о компании ({companyQuestions.length})
                    </h2>
                  </div>
                  <div className="mb-6">
                    <div className="space-y-4">
                      {companyQuestions.map((cq, index) => (
                        <CompanyQuestionCard
                          key={cq.id}
                          questionNumber={index + 1}
                          totalQuestions={companyQuestions.length}
                          question={cq.question}
                          answer={cq.answer}
                          onQuestionChange={(value) => {
                            setCompanyQuestions(prev => prev.map((q, i) => 
                              i === index ? { ...q, question: value } : q
                            ));
                          }}
                          onAnswerChange={(value) => {
                            setCompanyQuestions(prev => prev.map((q, i) => 
                              i === index ? { ...q, answer: value } : q
                            ));
                          }}
                          onDelete={() => {
                            if (companyQuestions.length <= 1) {
                              toast.error('Должен остаться хотя бы один вопрос');
                              return;
                            }
                            setCompanyQuestions(prev => prev.filter((_, i) => i !== index));
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Company Questions Buttons */}
              <div className="mb-6 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    toast('Находится в разработке', { icon: '🚧' });
                  }}
                  className="inline-flex items-center justify-center px-[14px] py-4 rounded-[16px] text-sm font-medium text-white"
                  style={{
                    background: 'linear-gradient(180deg, #E16349 0%, #DF1C41 100%)'
                  }}
                >
                  Сгенерировать с AI
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newCompanyQuestion = {
                      id: `company-q-${Date.now()}`,
                      question: '',
                      answer: ''
                    };
                    setCompanyQuestions(prev => [...prev, newCompanyQuestion]);
                  }}
                  className="inline-flex items-center justify-center px-[14px] py-4 rounded-[16px] text-sm font-medium border bg-white gap-2"
                  style={{ borderColor: colors.primary, color: colors.primary }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.16667 9.16667V5H10.8333V9.16667H15V10.8333H10.8333V15H9.16667V10.8333H5V9.16667H9.16667Z" fill="currentColor"/>
                  </svg>
                  Добавить вручную
                </button>
              </div>
            </>
          )}

          {!hideActions && (
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={isSaving || questions.length === 0}
                className="px-8 py-4 rounded-[30px] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => {
                  if (!isSaving && questions.length > 0)
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  if (!isSaving && questions.length > 0)
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary;
                }}
              >
                {isSaving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button
                onClick={handleCancel}
                className="px-8 py-4 rounded-[30px] border border-[#e2e4e9] bg-white hover:bg-[#f6f8fa] transition-colors font-medium"
              >
                Отмена
              </button>
            </div>
          )}
        </div>
      </div>
    </DndProvider>
  );
});
